import React, { useState } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPhone, faEnvelope, faMapMarkerAlt, faClock } from '@fortawesome/free-solid-svg-icons';
import { faInstagram, faFacebook, faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { submitContactForm } from '../services/api';

const PageContainer = styled.div`
  padding-top: 120px; // Space for fixed header
`;

const ContactContainer = styled.div`
  max-width: 1100px;
  margin: 40px auto;
  padding: 0 20px;
  
  @media (max-width: 768px) {
    margin: 20px auto;
  }
`;

const PageTitle = styled.h2`
  text-align: center;
  color: #145757;
  margin-bottom: 30px;
  font-size: 2.2rem;
`;

const ContactGridContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ContactCard = styled.div`
  background: white;
  padding: 30px;
  border-radius: 10px;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.08);
`;

const ContactTitle = styled.h3`
  color: #145757;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #eee;
`;

const ContactItem = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 20px;
`;

const ContactIcon = styled.div`
  margin-right: 15px;
  color: #145757;
  font-size: 20px;
  width: 25px;
  text-align: center;
`;

const ContactText = styled.div`
  flex: 1;
`;

const SocialIcons = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 30px;
`;

const SocialIcon = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: rgba(26, 95, 122, 0.1);
  color: #1a5f7a;
  text-decoration: none;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: #1a5f7a;
    color: white;
    transform: translateY(-3px);
  }
`;

const InputGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-family: inherit;
  font-size: 1rem;
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-family: inherit;
  font-size: 1rem;
  resize: vertical;
  min-height: 120px;
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 14px;
  background-color: #1a5f7a;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: #159895;
  }
  
  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const ThankYouMessage = styled.div`
  text-align: center;
  margin-top: 20px;
  padding: 15px;
  color: #155724;
  background-color: #d4edda;
  border-radius: 5px;
  display: ${props => props.show ? 'block' : 'none'};
`;

const ErrorMessage = styled.div`
  text-align: center;
  margin-top: 20px;
  padding: 15px;
  color: #721c24;
  background-color: #f8d7da;
  border-radius: 5px;
  display: ${props => props.show ? 'block' : 'none'};
`;

const MapContainer = styled.div`
  height: 400px;
  background: #eee;
  border-radius: 10px;
  overflow: hidden;
  margin-top: 30px;
  
  @media (max-width: 768px) {
    height: 300px;
  }
  
  iframe {
    width: 100%;
    height: 100%;
    border: 0;
  }
`;

const MapTitle = styled.h3`
  text-align: center;
  color: #145757;
  margin-bottom: 20px;
  font-size: 1.8rem;
`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  
  const [showThankYou, setShowThankYou] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      setErrorMessage('Please fill all fields');
      setShowError(true);
      setTimeout(() => setShowError(false), 5000);
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      // Submit form data
      await submitContactForm(formData);
      
      // Show thank you message and reset form
      setShowThankYou(true);
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
      
      // Hide thank you message after 5 seconds
      setTimeout(() => setShowThankYou(false), 5000);
    } catch (error) {
      console.error('Contact form error:', error);
      setErrorMessage('There was an error sending your message. Please try again.');
      setShowError(true);
      setTimeout(() => setShowError(false), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <PageContainer>
      <ContactContainer>
        <PageTitle>Contact Us</PageTitle>
        
        <ContactGridContainer>
          <ContactCard>
            <ContactTitle>Contact Information</ContactTitle>
            <ContactItem>
              <ContactIcon>
                <FontAwesomeIcon icon={faPhone} />
              </ContactIcon>
              <ContactText>
                <p><strong>Phone:</strong></p>
                <p>+92-303-0312222</p>
              </ContactText>
            </ContactItem>
            
            <ContactItem>
              <ContactIcon>
                <FontAwesomeIcon icon={faEnvelope} />
              </ContactIcon>
              <ContactText>
                <p><strong>Email:</strong></p>
                <p>terraceskyline1@gmail.com</p>
              </ContactText>
            </ContactItem>
            
            <ContactItem>
              <ContactIcon>
                <FontAwesomeIcon icon={faMapMarkerAlt} />
              </ContactIcon>
              <ContactText>
                <p><strong>Location:</strong></p>
                <p>Galiyat Region, Pakistan</p>
              </ContactText>
            </ContactItem>
            
            <ContactItem>
              <ContactIcon>
                <FontAwesomeIcon icon={faClock} />
              </ContactIcon>
              <ContactText>
                <p><strong>Office Hours:</strong></p>
                <p>Monday - Friday: 9am - 5pm</p>
                <p>Saturday: 10am - 2pm</p>
                <p>Sunday: Closed</p>
              </ContactText>
            </ContactItem>
            
            <SocialIcons>
              <SocialIcon href="https://instagram.com" target="_blank" rel="noopener noreferrer">
                <FontAwesomeIcon icon={faInstagram} />
              </SocialIcon>
              <SocialIcon href="https://facebook.com" target="_blank" rel="noopener noreferrer">
                <FontAwesomeIcon icon={faFacebook} />
              </SocialIcon>
              <SocialIcon href="https://wa.me/+923030312222" target="_blank" rel="noopener noreferrer">
                <FontAwesomeIcon icon={faWhatsapp} />
              </SocialIcon>
            </SocialIcons>
          </ContactCard>
          
          <ContactCard>
            <ContactTitle>Send Us a Message</ContactTitle>
            <p>Fill out the form below and we'll get back to you as soon as possible.</p>
            
            <form onSubmit={handleSubmit} style={{ marginTop: '20px' }}>
              <InputGroup>
                <Label htmlFor="name">Name *</Label>
                <Input 
                  type="text" 
                  id="name" 
                  name="name" 
                  value={formData.name}
                  onChange={handleChange}
                  required 
                />
              </InputGroup>
              
              <InputGroup>
                <Label htmlFor="email">Email *</Label>
                <Input 
                  type="email" 
                  id="email" 
                  name="email" 
                  value={formData.email}
                  onChange={handleChange}
                  required 
                />
              </InputGroup>
              
              <InputGroup>
                <Label htmlFor="subject">Subject *</Label>
                <Input 
                  type="text" 
                  id="subject" 
                  name="subject" 
                  value={formData.subject}
                  onChange={handleChange}
                  required 
                />
              </InputGroup>
              
              <InputGroup>
                <Label htmlFor="message">Message *</Label>
                <TextArea 
                  id="message" 
                  name="message" 
                  rows="5" 
                  value={formData.message}
                  onChange={handleChange}
                  required 
                />
              </InputGroup>
              
              <SubmitButton type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </SubmitButton>
              
              <ThankYouMessage show={showThankYou}>
                Thank you for your message! We'll get back to you soon.
              </ThankYouMessage>
              
              <ErrorMessage show={showError}>
                {errorMessage}
              </ErrorMessage>
            </form>
          </ContactCard>
        </ContactGridContainer>
        
        <MapTitle>Find Us</MapTitle>
        <MapContainer>
          <iframe 
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3307.9669748968526!2d73.39742551160018!3d33.98239557201723!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x38dfd70025746e55%3A0xa29183c041b92e43!2sKhaira%20Gali%2C%20Abbottabad%2C%20Khyber%20Pakhtunkhwa%2C%20Pakistan!5e0!3m2!1sen!2sus!4v1715618435645!5m2!1sen!2sus" 
            allowFullScreen="" 
            loading="lazy" 
            referrerPolicy="no-referrer-when-downgrade"
            title="Khaira Gali, Abbottabad Map"
          ></iframe>
        </MapContainer>
      </ContactContainer>
    </PageContainer>
  );
};

export default ContactPage;